<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { $bus, mqtt } from '@/common/events'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { EventEmitter } from 'events'
import { CabinetService } from "@/services/CabinetService";
import { useTranslate } from '@/composables/language'
import { L18CabinetControl } from '@/drivers/cabinet/PLT-L18.js'
import { L64CabinetControl } from '@/drivers/cabinet/PLT-L64.js'
import EventLog from '@/components/EventLog.vue'

let cabinet = null;

const props = defineProps({
  cabinet: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
});

const emit = defineEmits([ "onLog" ]);
function insertLog(record) {
  emit("onLog", record);
}

const mac = computed(() => {
  return props.cabinet ? props.cabinet.mac : null;
});

watch(mac, (newValue) => {
  initializeController();
});

watch(props.cabinet, (newValue) => {
  //console.log("watch cabinet", newValue)
});

async function initializeController() {
  if (mac.value) {
    if (cabinet) {
      cabinet.dispose();
    }

    cabinet = props.cabinet?.model == "plt-l64" ?
      new L64CabinetControl({ mqtt: mqtt, mac: mac.value }) :
      new L18CabinetControl({ mqtt: mqtt, mac: mac.value });

    //Get status when change occurs
    cabinet.on("status-change", async (data) => {
      appendEvent({ text: "status-change", type: "comment", direction: "inbound", time: getLogTime() });
      applyStatus(data);
    });

    cabinet.on("open", async (data) => {
      appendEvent({ text: "open: " + data?.doorNumber, type: "success", direction: "inbound", time: getLogTime() });
    });

    cabinet.on("closed", async (data) => {
      appendEvent({ text: "closed: " + data?.doorNumber, type: "command", direction: "inbound", time: getLogTime() });
    });

    cabinet.on("err", async (data) => {
      appendEvent({ text: "err: " + JSON.stringify(data), type: "danger", direction: "inbound", time: getLogTime() });
    });

    let transitionalEvents = [ "opening", "closing" ];
    for (let name of transitionalEvents) {
      cabinet.on(name, async (data) => {
        appendEvent({ text: name, type: "warning", direction: "inbound", time: getLogTime() });
      });
    }

    let genericEvents = [ "ref", "ack", "status", "door change" ];
    for (let name of genericEvents) {
      cabinet.on(name, async (data) => {
        appendEvent({ text: name, type: "comment", direction: "inbound", time: getLogTime() });
      });
    }

    //Get last status now
    applyStatus(await hcu.cabinet(mac.value).getStatus2());
  }
}

function applyStatus(data) {
  status.value = data;
  deviceStatus.value = data.deviceStatus || data.status; //init, connecting, open, closed, error
  // isOpen.value = data.isOpen;
  // isOpening.value = data.isOpening;
}

const status = ref(null);

const deviceStatus = ref(null);

const choice = ref('protocol');

function setChoice(value) {
  choice.value = value;
}

const { t } = useTranslate();

let cabinetService = new CabinetService();

let isTrayOpen = null;
const isModalVisible = ref(false);
const isOpening = ref(false);
const canResetLog = ref(true);

const openDelay = ref(null);
const placeholderHeight = ref(null);

const eventLogElement = ref();

const protocolLogElement = ref();
const protocolLogHeight = ref(300);

//Calculate event log height
function calculateEventLogHeight() {
  const minHeight = 150;
  const element = document.getElementById("protocolLog");
  const offset = element.getBoundingClientRect(); //Get element position within the window
  protocolLogHeight.value = Math.max(minHeight, window.innerHeight - offset.top - 20);
}

const maxDrumPosition = ref(24);

const currentDrumPosition = ref(null);
const currentDoorNumber = ref(null);
const selectedDrumPosition = ref(null);
const selectedDoorNumber = ref(null);
const openDoors = ref({ /* "1": false, "18": false */ });

const logRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);

const recentRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);

const eventRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);

let lastCommandRaw = null;
let lastCommandTime = null;
let insideRecurring = false;

const maxDoorNumber = computed(() => {
  const defaultMaxDoorNumber = props.cabinet?.model == "plt-l64" ? 64 : 18
  return props.cabinet?.maxDoorNumber > 0 ? parseInt(props.cabinet.maxDoorNumber) : defaultMaxDoorNumber;
});

const cabinetModel = computed(() => props.cabinet?.model);

const command = ref("");

onMounted(() => {
  initMQTT();

  initializeController();
  calculateEventLogHeight();
});

onUnmounted(() => {
  if (cabinet) {
    cabinet.dispose();
  }
});

function getLogTime() {
  let now = new Date();
  let time = now.toISOString().split("T")[1].substr(0, 8);
  return time;
}

function invokeCommand() {
  if (command.value.trim().length > 0) {
    recentRecords.value = [ ];

    cabinet.invokeCommand(command.value);
    
    lastCommandRaw = command.value;
    lastCommandTime = now;

    appendLog({ text: command.value, type: "command", direction: "outbound", time: getLogTime() });
    command.value = "";
  }
}

function clearRecentLogs() {
  recentRecords.value = [ ];
  command.value = "";
}

async function selectDoor(i) {
  selectedDoorNumber.value = i;
  await openTray(i);
}

/*
function openTray() {
  if (isOpening.value) {
    return;
  }
  isOpening.value = true;

  (async () => {
    try {
      let index = trayIndex.value;
      let data = await cabinetService.openTray({ trayIndex: index });
      startChecking();
    }
    catch (err) {
      //alert(err.message);
    }
  })();
}
*/

async function openTray(doorNumber) {
  if (!mac.value) {
    dialog(new Error(t('service.mac_not_set')));
    return;
  }

  // if (isOpening.value) {
  //   return;
  // }
  // isOpening.value = true;

  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    //logRecords.value.push({ text: `${doorNumber} ${drumPosition}`, type: "command", direction: "outbound", time: time });
    //let data = await cabinetService.openDrum({ drumPosition, doorNumber });

    //TODO: Only when mqtt is ready
    //TODO: Verify door number

    try {
      let trayName = "" + doorNumber;
      insertLog({
        uid: props.user?.id,
        tn: trayName,
        cid: props.cabinet?.id,
        msg: "Servisno okno: odpiranje predala " + trayName + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }

    //socket.emit("publish", { topic: "hcu/cabinet/drum/open", payload:{ drumPosition, doorNumber } });
    //mqtt.publish({ topic: "hcu/cabinets/" + mac.value + "/open/request", payload: { doorNumber } });
    //useCabinetControl(mac.value).openTray({ doorNumber });
    cabinet.openTray({ doorNumber }, { timeout: false });

    /*
    setTimeout(function() {
      isOpening.value = false;
    }, 5000);
    */
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

function updateDrumSelection(data) {
  selectedDrumPosition.value = data.position;
}

function start() {
  let drum = selectedDrumPosition.value;
  let door = selectedDoorNumber.value;
  openDrum(drum, door);
}

async function openAll() {
  try {
    if (isOpening.value) {
      return;
    }
    isOpening.value = true;
    isModalVisible.value = false;

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;

    function delay(ms) {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    }

    let postpone = openDelay.value > 0 ? openDelay.value : 500;

    try {
      insertLog({
        uid: props.user?.id,
        cid: props.cabinet?.id,
        msg: "Servisno okno: odpiranje vseh predalov" + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }

    for (let i = 1; i <= maxDoorNumber.value; i++) {
      let doorNumber = i;
      mqtt.publish({ topic: "hcu/cabinets/" + mac.value + "/open/request", payload: { doorNumber } });
      await delay(postpone);
    }
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    dialog(err);
  }
}

function clearLogs() {
  logRecords.value = [ ];
  eventRecords.value = [ ];
  isModalVisible.value = false;
}

function clearTimer() {
  isOpening.value = false;
  isModalVisible.value = false;
}

function moveToReferencePoint() {
  //reset();
  canResetLog.value = false;
  //mqtt.publish({ topic: "hcu/cabinet/" + mac.value + "/ref" });
  //socket.emit("publish", { topic: "hcu/command/cabinet/drum/ref" });
  cabinet.goToRef();
}

function getStatus() {
  //mqtt.publish({ topic: "hcu/cabinets/" + mac.value + "/status/request" });
  //socket.emit("publish", { topic: "hcu/command/cabinet/drum/status" });
  cabinet.requestStatus();
}

function showModal() {
  isModalVisible.value = true;
}

function closeModal() {
  isModalVisible.value = false;
}

function initMQTT() {
  mqtt.ready(() => {
  //mqtt.init(); //Already performed in App.vue
    mqtt.on('connect', () => {
      //responses.value.push({ color: "yellow", topic: "Socket", payload: "connected" });
    });
    mqtt.on('disconnect', () => {
      //responses.value.push({ color: "red", topic: "Socket", payload: "disconnected" });
    });
    mqtt.on('received', (data) => {
      //console.log("received", data);

      if (data.topic == "hcu/cabinets/" + mac.value + "/debug") {
        //data.payload.type == "io"
        //console.log("debug", data.payload);

        let now = new Date();
        let elapsed = null;

        let type = "warning";
        if (data.payload.text == "OK" || data.payload.text == "DONE" || data.payload.text == "READY" || data.payload.text == "ACK") {
          type = "success";
          if (lastCommandTime) {
            elapsed = now.getTime() - lastCommandTime.getTime();
          }
          if (data.payload.text == "DONE" || data.payload.text == "READY") {
            isOpening.value = false;
            canResetLog.value = true;
          }
        }
        else if (data.payload.text == "PER" || data.payload.text == "END_PER" || data.payload.text == "EVT" || data.payload.text == "END_EVT" || data.payload.text == "Door open") {
          type = "comment";
        }
        else if (data.payload.text == "ERR" || data.payload.text == "ERR_ON_CLOSET" || data.payload.text == "Close and continue") {
          type = "danger";
          if (lastCommandTime) {
            elapsed = now.getTime() - lastCommandTime.getTime();
          }
          isOpening.value = false;
        }
        else if (/^\d+\s\d+\r?\n?$/g.test(data.payload.text) || data.payload.text == "S" || data.payload.text == "R" || data.payload.text == "20" || data.payload.text == "19 25") {
          type = "command";
          lastCommandTime = now;
        }
        else if (/^([01]{18,64})\r?\n?$/g.test(data.payload.text)) {
          let bits = data.payload.text.trim();
          for (let i = 0; i < bits.length; i++) {
            let index = (bits.length - i).toString();
            let value = bits[i] == "1";
            openDoors.value[index] = value;
          }
        }
        let time = now.toISOString().split("T")[1].substr(0, 8);

        if (data.payload.text == "PER") {
          insideRecurring = true;
        }
        if (data.payload.text == "END_PER") {
          insideRecurring = false;
        }
        if (data.payload.text && !insideRecurring && data.payload.text != "END_PER") {
          //Check if echo door number
          if (/^[0-9]{1,2}$/.test(data.payload.text) || data.payload.text == "STA") {
            appendLog({
              text: data.payload.text,
              time: time,
              type: "command",
              direction: "outbound",
              elapsed: null,
            });
          }
          else {
            appendLog({
              text: data.payload.text,
              time: time,
              type: type,
              direction: data.payload.dir == "in" ? "inbound" : "outbound",
              elapsed: elapsed ? `+${elapsed}ms` : null,
            });
          }
        }
      }
      
    });
  });
  
  /*
    { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
    { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
    { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
    { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
    { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
    { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
  */
}

function appendLog(record) {
  logRecords.value.push(record);
  if (lastCommandRaw) {
    recentRecords.value.push(record);
  }
}

function appendEvent(record) {
  eventRecords.value.push(record);
}

function sendRaw(command) {
  let now = new Date();
  let time = now.toISOString().split("T")[1].substr(0, 8);
  appendLog({ text: command, time: time, type: "command", direction: "outbound" });
  cabinet.invokeCommand(command);
}
</script>

<template>
  <div class="relative dark:text-white">

    <div class="">
      <!-- Top right corner -->
      <div class="flex flex-row justify-between space-x-2 mt-4">

        <div class="grid place-items-center">
          <div class="whitespace-nowrap">
            {{ $t('service.locker') }}
            <span class="font-bold" v-if="props.cabinet && props.cabinet.model">{{ props.cabinet.model.toUpperCase() }}</span>
            <!-- <span class="font-mono text-blue-500 ml-2" v-if="props.cabinet">{{ props.cabinet.mac }}</span>
            <span class="font-mono text-purple-500 ml-2" v-if="props.cabinet">{{ props.cabinet.protocol || props.cabinet.type }} {{ props.cabinet.host }}:{{ props.cabinet.port }}</span> -->
            <!-- <span class="ml-2">
              <i class="fa fa-regular fa-lightbulb text-yellow-300 mt-1 animate-bounce" v-if="deviceStatus == 'init' || deviceStatus == 'connecting'"></i>
              <i class="fa fa-solid fa-lightbulb text-red-600 mt-1" v-else-if="deviceStatus == 'closed' || deviceStatus == 'error'"></i>
              <i class="fa fa-solid fa-lightbulb text-green-400 mt-1" v-else-if="deviceStatus == 'open'"></i>
              <i class="fa fa-regular fa-lightbulb text-slate-400 mt-1" v-else></i>
            </span> -->
          </div>
        </div>

        <!-- Action button -->
        <div class="flex flex-row justify-items-strech space-x-2">
          <span class="ml-1 mr-1 mt-2">
            <i class="fa fa-power-off text-yellow-300 mt-1 animate-bounce" v-if="deviceStatus == 'init' || deviceStatus == 'connecting'"></i>
            <i class="fa fa-power-off text-red-600 mt-1" v-else-if="deviceStatus == 'closed' || deviceStatus == 'error'"></i>
            <i class="fa fa-power-off text-green-400 mt-1" v-else-if="deviceStatus == 'open'"></i>
            <i class="fa fa-power-off text-slate-400 mt-1" v-else></i>
          </span>
          <Button type="secondary" @click="showModal">{{ $t('service.more') }}</Button>
          <Button type="secondary" @click="getStatus">{{ $t('service.status_label') }}</Button>
          <Button type="secondary" :enabled="false" @click="moveToReferencePoint">{{ $t('service.reference') }}</Button>
        </div>

      </div>
    </div>

    <!-- Content -->
    <div class="mt-4 w-full">

      <!-- Various service commands -->
      <div v-if="cabinetModel == 'plt-l64'">
        <div>{{ $t('service.commands_label') }}</div>
        <div class="mb-4">
          <div class="flex flex-wrap gap-2 mt-4 mb-2">
            <Button type="secondary" @click="sendRaw('PING')">PING</Button>
            <Button type="secondary" @click="sendRaw('INF')">INF</Button>
            <Button type="secondary" @click="sendRaw('STA')">STA</Button>
            <Button type="secondary" @click="sendRaw('MODE?')">MODE</Button>
            <Button type="secondary" @click="sendRaw('SCAN')">SCAN</Button>
          </div>
        </div>
      </div>

      <!-- Door number -->
      <div v-if="maxDoorNumber > 0">
        <div>{{ $t('service.click_door_to_open') }}</div>
        <div class="mb-4">
          <div class="flex flex-wrap gap-2 mt-4 mb-2">
            <a href="#" v-for="i in maxDoorNumber" :key="'dl' + i" @click.prevent="selectDoor(i)" class="button-door" :class="{ 'button-door-selected': i == selectedDoorNumber, 'button-door-position': !!openDoors[i.toString()] }">
              <h5 class="button-door-label">{{ i }}</h5>
            </a>
          </div>
        </div>
      </div>

      <!-- Logger -->
      <div class="w-full h-full">

        <div class="w-full border-b mb-4 dark:border-gray-700">
          <button class="choice-button" :class="{ 'choice-active': choice == 'protocol' }" @click="setChoice('protocol')">{{ $t('service.tab_protocol') }}</button>
          <button class="choice-button" :class="{ 'choice-active': choice == 'status' }" @click="setChoice('status')">{{ $t('service.tab_status') }}</button>
          <button class="choice-button" :class="{ 'choice-active': choice == 'console' }" @click="setChoice('console')">{{ $t('service.tab_console') }}</button>
          <button class="choice-button" :class="{ 'choice-active': choice == 'events' }" @click="setChoice('events')">{{ $t('service.tab_events') }}</button>
        </div>

        <!-- Protocol -->
        <EventLog id="protocolLog" ref="protocolLogElement" :records="logRecords" :style="'height: ' + protocolLogHeight + 'px'" v-if="choice == 'protocol'">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>

        <!-- Status -->
        <pre class="px-3 py-3 mb-10 border rounded bg-amber-100 text-amber-700 text-xs font-mono" v-if="choice == 'status'">{{ status }}</pre>

        <!-- Console -->
        <div class="w-full" v-if="choice == 'console'">
          <div class="flex flex-row gap-2 mb-2">
            <TextBox v-model="command" :placeholder="$t('service.enter_command')" class="w-full" @keypress.enter="invokeCommand()" />
            <Button type="secondary" @click="clearRecentLogs()">{{ $t('generic.clear') }}</Button>
            <Button type="primary" @click="invokeCommand()">{{ $t('service.send') }}</Button>
          </div>
          <EventLog :records="recentRecords" :style="'height: ' + (protocolLogHeight - 50) + 'px'">
            <template #empty>
              {{ $t('service.no_logs') }}
            </template>
          </EventLog>
        </div>

        <!-- Events -->
        <EventLog id="eventLog" ref="eventLogElement" :records="eventRecords" :style="'height: ' + protocolLogHeight + 'px'" v-if="choice == 'events'">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>

      </div>

    </div>

    <Modal size="xl" v-if="isModalVisible" @close="closeModal">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('service.more_options') }}
        </div>
      </template>
      <template #body>
        <div class="flex flex-col w-full gap-2">

          <div class="mt-0">
            <span>{{ $t('service.open_delay_label') }}</span>
          </div>
          <div class="grid grid-cols-5 gap-2">
            <TextBox v-model="openDelay" placeholder="500 ms" class="col-span-3"></TextBox>
            <Button type="primary" class="col-span-2" @click="openAll">{{ $t('service.open_all_trays') }}</Button>
          </div>

          <div class="mt-4">
            <span>{{ $t('service.clear_logs_text') }}</span>
          </div>
          <div class="mt-0">
            <Button type="primary" @click="clearLogs">{{ $t('generic.clear') }}</Button>
          </div>

        </div>
      </template>
    </Modal>

  </div>
</template>

<style scoped>
a {
  outline: none;
}

.label {
  position: relative;
  top: 5px;
  height: 30px;
  line-height: 30px;
}

.choice-button {
  @apply px-5 py-3;
}

.choice-button.choice-active {
  border-bottom: 3px solid rgb(26 86 219);
}

.button-circle {
  @apply aspect-square p-4 font-medium border border-gray-200 rounded-lg shadow-md;
  /* @apply bg-white hover:bg-gray-100 */ /* White button */
  @apply text-white bg-blue-700 hover:bg-blue-800; /* Blue button */
  position: absolute;
  /* left: 260px; */
  /* top: 260px; */
  left: 165px;
  top: 165px;
  z-index: 10;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  font-size: 24px;
}

.button-circle.button-circle-static,
.button-circle.button-circle-static:hover {
  @apply bg-white border-gray-200 hover:bg-white hover:border-gray-200;
  cursor: default;
}

.button-circle.button-circle-inactive,
.button-circle.button-circle-inactive:hover {
  @apply bg-gray-100 text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
  cursor: default;
}

.button-door {
  @apply w-16;
  @apply flex justify-center aspect-square p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.button-door-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

@media only screen and (max-width: 1024px) { 
  .button-door {
    @apply w-14 p-3;
  }

  .button-door-label {
    @apply text-lg;
  }
}

.button-door.button-door-selected {
  border: 2px solid rgba(26,86,219,1);
}

.button-door.button-door-position,
.button-door.button-door-position:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}
</style>
